import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BRAND } from '@br/protocol';
import { errorMessage } from '../api/client';
import { useJoin, useJoinMe } from '../api/queries';
import { Alert, Button, Input } from '../components/ui';
import { formatInviteCode, isCompleteInviteCode } from '../lib/format';
import { usePageTitle } from '../lib/title';

/** Customer entry: support-session link (`/join/:token`) or short code (`/join`). */
export function JoinPage() {
  usePageTitle('접속');
  const { token } = useParams<{ token?: string }>();
  const me = useJoinMe();
  const join = useJoin();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const name = label.trim();
    if (!name) return setError('이름을 입력해주세요');
    if (!token && !isCompleteInviteCode(code)) return setError('접속 코드 8자리를 입력해주세요');
    join.mutate(token ? { token, label: name } : { code, label: name }, {
      onSuccess: (result) => navigate(`/support/${result.profileId}`, { replace: true }),
      onError: (err) => setError(errorMessage(err)),
    });
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-line bg-surface p-8 shadow-sm sm:p-10">
        <p className="text-sm font-semibold text-primary">{BRAND.name}</p>
        <h1 className="mt-2 text-2xl font-bold text-ink">원격 지원 접속</h1>
        <p className="mt-2 text-base leading-relaxed text-ink-muted">
          지원 담당자가 보내드린 {token ? '링크로 접속하셨습니다. ' : '접속 코드와 '}
          이름을 입력하면 준비된 브라우저 화면이 열립니다.
        </p>

        {me.data && (
          <div className="mt-6 rounded-xl bg-primary-soft p-4 text-sm text-blue-900">
            <p>
              <strong>{me.data.participantLabel}</strong>님, 이미 접속 중인 세션이 있습니다.
            </p>
            <Button
              className="mt-3"
              variant="primary"
              onClick={() => navigate(`/support/${me.data!.profileId}`)}
            >
              이어서 보기
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-5">
          {!token && (
            <label className="block">
              <span className="mb-1.5 block text-base font-medium text-ink">접속 코드</span>
              <Input
                className="h-14 text-center font-mono text-2xl tracking-[0.3em] uppercase"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                placeholder="K7PX-4MQZ"
                value={code}
                onChange={(e) => setCode(formatInviteCode(e.target.value))}
                autoFocus
              />
            </label>
          )}
          <label className="block">
            <span className="mb-1.5 block text-base font-medium text-ink">이름</span>
            <Input
              className="h-14 text-lg"
              placeholder="지원 담당자가 알아볼 수 있는 이름"
              maxLength={64}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus={Boolean(token)}
            />
          </label>
          {error && <Alert level="error">{error}</Alert>}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={join.isPending}
          >
            접속하기
          </Button>
        </form>
        <p className="mt-6 text-xs leading-relaxed text-ink-muted">
          이 화면에서는 지원 담당자가 준비한 웹 페이지만 보고 조작할 수 있습니다. 컴퓨터의 파일이나
          다른 프로그램에는 접근하지 않습니다.
          {token && (
            <>
              {' '}
              링크 대신 코드가 있다면{' '}
              <Link to="/join" className="text-primary underline">
                코드로 접속
              </Link>
              하세요.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
